import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import useVehicleScheduleStore, { RENTAL_VEHICLE_ID } from '../stores/useVehicleScheduleStore';
import { useToast } from '../components/Toast/ToastContext';
import { VehicleScheduleEvent } from './types';
import { formatEventDate } from '../utils/date-utils';
import { buttonClass } from '../components/ui/Button';
import Reveal from '../components/ui/Reveal';
import Tooltip from '../components/ui/Tooltip';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  vehicleId: string;
  vehicleName: string;
  dateStr: string;
  /** Producers can remove existing assignments / blocks (cleanup, any status). */
  canEdit: boolean;
  /** Assign + mark-maintenance — producers only AND only for usable vehicles
   *  (retired / OOC rigs are not assignable going forward). */
  canAssign: boolean;
  /** Events whose show span covers this day (the assignable pool). */
  eventsOnDay: VehicleScheduleEvent[];
  /** This vehicle's assignments whose band covers this day. */
  assignedEvents: VehicleScheduleEvent[];
  /** Maintenance block covering this day, if any. */
  block: { id: string; notes: string; start: string; end: string } | null;
  position: { top: number; left: number };
}

const VehicleCellPopover: React.FC<Props> = ({
  isOpen, onClose, vehicleId, vehicleName, dateStr,
  canEdit, canAssign, eventsOnDay, assignedEvents, block, position,
}) => {
  const assignments = useVehicleScheduleStore(s => s.assignments);
  const assignVehicle = useVehicleScheduleStore(s => s.assignVehicle);
  const unassignVehicle = useVehicleScheduleStore(s => s.unassignVehicle);
  const setTransportArranged = useVehicleScheduleStore(s => s.setTransportArranged);
  const createBlock = useVehicleScheduleStore(s => s.createBlock);
  const deleteBlock = useVehicleScheduleStore(s => s.deleteBlock);
  const toast = useToast();
  const [markingBlock, setMarkingBlock] = useState(false);
  const [blockEnd, setBlockEnd] = useState(dateStr);
  const [blockNotes, setBlockNotes] = useState('');
  const [busy, setBusy] = useState(false);

  // The Shipping / Rentals lanes are not a vehicle — they are the "transport is
  // handled outside the fleet" flag on the event. Same picker, different verb:
  // there is nothing to assign and nothing to take out of service, so the action
  // is transport_arranged and the maintenance section does not apply.
  const isRentalLane = vehicleId === RENTAL_VEHICLE_ID;
  const hasBlock = !!block && !isRentalLane;

  if (!isOpen) return null;

  const assignedIds = new Set(assignedEvents.map(e => e.id));
  const unassignedEvents = eventsOnDay.filter(e => !assignedIds.has(e.id));

  // What transport a show in the "Assign to" list ALREADY has, so the producer can
  // see this click adds to it rather than replacing it. A truck and a shipping /
  // rental are two resources on one show, and the grid draws both — without this
  // hint the second one looks like it might swap the first out.
  const eventsWithTruck = new Set(assignments.map(a => a.event_id));
  const alreadyHas = (evt: VehicleScheduleEvent): string | null => {
    if (isRentalLane) return eventsWithTruck.has(evt.id) ? 'has truck' : null;
    const parts: string[] = [];
    // A show already on ANOTHER truck: a second truck is legal (main + meet-up),
    // and this is the click where add-vs-swap is most ambiguous.
    if (assignments.some(a => a.event_id === evt.id && a.vehicle_id !== vehicleId)) parts.push('truck');
    if (evt.transport_arranged) parts.push(evt.transport_mode === 'driving' ? 'rental' : 'shipping');
    return parts.length ? `has ${parts.join(' + ')}` : null;
  };

  const handleAssign = async (event: VehicleScheduleEvent) => {
    setBusy(true);
    const res = isRentalLane
      ? await setTransportArranged(event.id, true)
      : await assignVehicle(event.id, vehicleId);
    setBusy(false);
    if (res.ok) {
      toast.success(isRentalLane ? `${event.name} → shipping / rental` : `${vehicleName} → ${event.name}`);
      onClose();
    } else {
      toast.error(res.error || (isRentalLane ? 'Failed to mark transport arranged' : 'Failed to assign vehicle'));
    }
  };

  const handleUnassign = async (event: VehicleScheduleEvent) => {
    setBusy(true);
    const res = isRentalLane
      ? await setTransportArranged(event.id, false)
      : await unassignVehicle(event.id, vehicleId);
    setBusy(false);
    if (res.ok) { toast.success(`Removed from ${event.name}`); onClose(); }
    else toast.error(res.error || 'Failed to remove');
  };

  const handleConfirmBlock = async () => {
    const end = blockEnd && blockEnd >= dateStr ? blockEnd : dateStr;
    setBusy(true);
    const res = await createBlock(vehicleId, dateStr, end, blockNotes.trim());
    setBusy(false);
    if (res.ok) { toast.success('Maintenance block added'); onClose(); }
    else toast.error(res.error || 'Failed to add maintenance block');
  };

  const handleRemoveBlock = async () => {
    if (!block) return;
    setBusy(true);
    const res = await deleteBlock(block.id);
    setBusy(false);
    if (res.ok) { toast.success('Maintenance block removed'); onClose(); }
    else toast.error(res.error || 'Failed to remove maintenance block');
  };

  return (
    <>
      <div className="fixed inset-0 z-[1000]" onClick={onClose} />
      <div
        className="fixed z-[1001] bg-gray-850 day:bg-white border border-gray-700 day:border-gray-200 rounded-lg shadow-overlay day:shadow-xl day:shadow-gray-900/10 min-w-[210px] max-w-[280px]"
        style={{ top: position.top, left: position.left }}
      >
        <div className="px-3 py-2 border-b border-gray-700/60 day:border-gray-200">
          <div className="text-sm font-medium text-on-surface day:text-gray-800">{vehicleName}</div>
          <div className="text-xs text-on-surface-variant day:text-gray-500">{formatEventDate(dateStr)}</div>
        </div>

        <div className="px-2 py-2 space-y-1">
          {/* Current assignments on this day */}
          {assignedEvents.length > 0 && (
            <>
              <div className="px-1 text-[10px] text-on-surface-variant day:text-gray-500 uppercase tracking-wider">Assigned</div>
              {assignedEvents.map(evt => (
                <div key={evt.id} className="px-1 py-1.5 rounded hover:bg-surface-container-highest day:hover:bg-gray-100">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-medium text-on-surface day:text-gray-800 truncate">{evt.name}</span>
                    {canEdit && (
                      <button
                        onClick={() => handleUnassign(evt)}
                        disabled={busy}
                        className={`${buttonClass('danger-quiet', 'sm')} shrink-0`}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <Link
                    to={`/events/${evt.id}`}
                    onClick={onClose}
                    className="text-[10px] text-on-surface-variant day:text-gray-500 hover:text-primary day:hover:text-amber-700 transition-colors mt-0.5 inline-block"
                  >
                    View Event &rarr;
                  </Link>
                </div>
              ))}
            </>
          )}

          {/* Events to assign on this day (usable vehicles only) */}
          {canAssign && unassignedEvents.length > 0 && !hasBlock && (
            <>
              <div className="px-1 text-[10px] text-on-surface-variant day:text-gray-500 uppercase tracking-wider pt-1">Assign to</div>
              {unassignedEvents.map(evt => {
                const has = alreadyHas(evt);
                return (
                <div key={evt.id} className="flex items-center gap-1">
                  <button
                    onClick={() => handleAssign(evt)}
                    disabled={busy}
                    className="flex-1 text-left px-2 py-1 rounded text-xs font-medium bg-gray-700/40 day:bg-gray-100 text-on-surface day:text-gray-800 hover:bg-gray-700/70 day:hover:bg-gray-200 transition-colors truncate disabled:opacity-50"
                  >
                    {evt.name}
                    {/* Before the drone count on purpose: the row is `truncate`, so
                        the tail is what the ellipsis eats. Of the two, the hint is the
                        one that stops a wrong click; the count is informational. */}
                    {has && <span className="opacity-50 ml-1 font-normal">· {has}</span>}
                    <span className="opacity-50 ml-1">({evt.ls_drone_count + evt.pyro_drone_count})</span>
                  </button>
                  <Link
                    to={`/events/${evt.id}`}
                    onClick={onClose}
                    className="text-[10px] text-on-surface-disabled day:text-gray-500 hover:text-primary day:hover:text-amber-700 transition-colors px-1 shrink-0"
                    title="View event"
                  >
                    &rarr;
                  </Link>
                </div>
                );
              })}
            </>
          )}

          {/* Read-only list (viewers, or producers on a retired/OOC vehicle) */}
          {!canAssign && unassignedEvents.length > 0 && (
            <>
              <div className="px-1 text-[10px] text-on-surface-variant day:text-gray-500 uppercase tracking-wider pt-1">Shows this day</div>
              {unassignedEvents.map(evt => (
                <Link
                  key={evt.id}
                  to={`/events/${evt.id}`}
                  onClick={onClose}
                  className="block px-2 py-1 rounded text-xs text-on-surface day:text-gray-700 hover:bg-surface-container-highest day:hover:bg-gray-100 truncate"
                >
                  {evt.name} &rarr;
                </Link>
              ))}
            </>
          )}

          {/* Maintenance block affordance (usable vehicles only).
              NOT on a rental lane: RENTAL_VEHICLE_ID is a sentinel, not an asset, so
              createBlock() would POST '__rental__' as asset_id and come back 400
              "asset_id must be a UUID" — a developer string in a red toast. `hasBlock`
              was already rental-aware; this create path was not. */}
          {canAssign && !isRentalLane && !hasBlock && (
            <div className="px-1 pt-1">
              <Reveal show={!markingBlock}>
                <Tooltip label="Mark this vehicle out of commission for maintenance"><button
                  onClick={() => setMarkingBlock(true)}
                  className={`${buttonClass('quiet', 'sm')} w-full`}
                >
                  Mark maintenance…
                </button></Tooltip>
              </Reveal>
              <Reveal show={markingBlock}>
                <div className="px-2 py-1.5 rounded border bg-slate-700/40 day:bg-slate-100 border-slate-500/70 day:border-slate-300 space-y-1.5">
                  <div className="text-[10px] text-slate-200 day:text-slate-700">
                    From {formatEventDate(dateStr)} through:
                  </div>
                  <input
                    type="date"
                    value={blockEnd}
                    min={dateStr}
                    onChange={(e) => setBlockEnd(e.target.value)}
                    className="w-full text-xs px-1.5 py-1 rounded bg-gray-900/40 day:bg-white border border-slate-500/40 day:border-slate-300 text-on-surface day:text-gray-900 outline-none focus:ring-1 focus:ring-slate-400"
                  />
                  <textarea
                    value={blockNotes}
                    onChange={(e) => setBlockNotes(e.target.value)}
                    placeholder="Optional note (e.g. transmission service)"
                    rows={2}
                    className="w-full text-xs px-1.5 py-1 rounded bg-gray-900/40 day:bg-white border border-slate-500/40 day:border-slate-300 text-on-surface day:text-gray-900 placeholder:text-on-surface-variant outline-none focus:ring-1 focus:ring-slate-400 resize-none"
                  />
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => { setMarkingBlock(false); setBlockNotes(''); setBlockEnd(dateStr); }}
                      className={buttonClass('quiet', 'sm')}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleConfirmBlock}
                      disabled={busy}
                      className={buttonClass('filled', 'sm')}
                    >
                      {busy ? 'Adding…' : 'Add block'}
                    </button>
                  </div>
                </div>
              </Reveal>
            </div>
          )}

          {block && (
            <div className="px-1 pt-1">
              <div className="text-[10px] text-on-surface-variant day:text-gray-500 uppercase tracking-wider mb-1">Maintenance</div>
              <div className="px-2 py-1.5 rounded border bg-slate-700/40 day:bg-slate-100 text-slate-100 day:text-slate-700 border-slate-500/70 day:border-slate-300 flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="truncate font-medium text-xs">{block.notes || 'Out of commission'}</div>
                  <div className="text-[10px] opacity-70 mt-0.5">
                    {block.start === block.end
                      ? formatEventDate(block.start)
                      : `${formatEventDate(block.start)} – ${formatEventDate(block.end)}`}
                  </div>
                </div>
                {canEdit && (
                  <button
                    onClick={handleRemoveBlock}
                    disabled={busy}
                    className={`${buttonClass('danger-quiet', 'sm')} shrink-0`}
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          )}

          {eventsOnDay.length === 0 && assignedEvents.length === 0 && !hasBlock && !markingBlock && (
            <div className="px-1 py-1 text-[11px] text-on-surface-variant day:text-gray-500 text-center">No events on this day</div>
          )}
        </div>
      </div>
    </>
  );
};

export default VehicleCellPopover;
